import React, { useEffect, useState } from "react";
import Card from "components/card";
import Button from "components/button";
import { useAuthContext } from "hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { updateCurrentUser } from "services/users";
import { getAgeGrades } from "services/ageGrades";
import { useToast } from "components/Toast";

const UserProfile: React.FC = () => {
  const { user, setUser } = useAuthContext();
  const toast = useToast();
  const [ageGrades, setAgeGrades] = useState<any[]>([]);
  const [form, setForm] = useState({
    name: user?.name || "",
    firstname: user?.firstname || "",
    lastname: user?.lastname || "",
    email: user?.email || "",
    phone: user?.phone || "",
    dob: (user as any)?.dob || "",
    age_grade_id: (user as any)?.age_grade_id || "",
    password: "",
    password_confirmation: "",
    pfp: user?.pfp || "",
  });
  const [pfpFile, setPfpFile] = useState<File | null>(null);
  const [pfpPreview, setPfpPreview] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getAgeGrades();
        const data = res.data?.data ?? res.data ?? [];
        if (mounted) setAgeGrades(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (mounted) setAgeGrades([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload: any = { ...form };
      if (!payload.password) {
        delete payload.password;
        delete payload.password_confirmation;
      }
      // Handle profile photo upload
      let updatedUser: any;
      if (pfpFile) {
        const formData = new FormData();
        formData.append("pfp", pfpFile);
        // Append other fields
        Object.keys(payload).forEach((key) => {
          if (key !== "pfp") formData.append(key, payload[key]);
        });
        updatedUser = await updateCurrentUser(formData);
      } else {
        updatedUser = await updateCurrentUser(payload);
      }
      // Set form.pfp to backend path after upload and update global user context
      if (updatedUser && updatedUser.pfp) {
        setForm((f) => ({ ...f, pfp: updatedUser.pfp }));
        setPfpPreview(""); // clear preview after save
        if (setUser) setUser(updatedUser);
      }
      toast.showSuccess?.("Profile updated");
    } catch (err) {
      console.error(err);
      toast.showError?.("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const navigate = useNavigate();
  const { logout } = useAuthContext();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.showError?.("Logout failed");
    }
  };

  return (
    <Card extra="w-full p-4">
      <h1 className="mb-4 text-2xl font-bold">Profile</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        encType="multipart/form-data"
      >
        {/* Profile Photo Upload */}
        <div className="mb-2 flex flex-col items-start">
          <label className="mb-1 font-medium">Profile Photo</label>
          {/* Show preview if a new file is selected, else show backend pfp */}
          {pfpPreview ? (
            <img
              src={pfpPreview}
              alt="Profile preview"
              className="mb-2 h-20 w-20 rounded-full object-cover"
            />
          ) : form.pfp ? (
            <img
              src={
                form.pfp.startsWith("http") ? form.pfp : `/storage/${form.pfp}`
              }
              alt="Profile"
              className="mb-2 h-20 w-20 rounded-full object-cover"
            />
          ) : null}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setPfpFile(e.target.files[0]);
                setPfpPreview(URL.createObjectURL(e.target.files[0]));
              }
            }}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="firstname"
            value={form.firstname}
            onChange={handleChange}
            className="input"
            placeholder="First name"
          />
          <input
            name="lastname"
            value={form.lastname}
            onChange={handleChange}
            className="input"
            placeholder="Last name"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input"
            placeholder="Email"
          />
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="input"
            placeholder="Phone"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="dob"
            value={form.dob}
            onChange={handleChange}
            className="input"
            placeholder="Date of birth"
            type="date"
          />
          <select
            name="age_grade_id"
            value={form.age_grade_id}
            onChange={handleSelect}
            className="input"
          >
            <option value="">Select age grade</option>
            {ageGrades.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name || g.title || g.label}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="password"
            value={form.password}
            onChange={handleChange}
            className="input"
            placeholder="New password (leave blank to keep)"
            type="password"
          />
          <input
            name="password_confirmation"
            value={form.password_confirmation}
            onChange={handleChange}
            className="input"
            placeholder="Confirm password"
            type="password"
          />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button type="button" color="red" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default UserProfile;
